import { ConfigurableFocusTrapFactory } from "@angular/cdk/a11y";
import { DOCUMENT } from "@angular/common";
import {
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Inject,
	Input,
	OnInit,
	Optional,
	Output,
	ViewContainerRef,
} from "@angular/core";
import { UntypedFormControl, Validators } from "@angular/forms";
import { resolveClientDisplayColor } from "@app/_helpers/client-color";
import { hasPermissionByKey, isProjectUser } from "@app/_helpers/permission";
import { createRxValue, fromRxValue } from "@app/_helpers/utils";
import { DefaultEntityTranslatePipe } from "@app/_pipes/default-entity-translate.pipe";
import { AppService } from "@app/app.service";
import { SatPopoverComponent as SatPopover, SatPopoverAnchoringService } from "@ncstate/sat-popover";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Project, ProjectsQuery, ProjectsService, UserSettingsQuery } from "@timeghost/web-api";
import { xor } from "lodash-es";
import { BehaviorSubject, combineLatest } from "rxjs";
import { distinctUntilChanged, map, startWith } from "rxjs/operators";
import { firstBy } from "thenby";

@UntilDestroy()
@Component({
	selector: "tg-project-filter-popover",
	templateUrl: "./project-filter-popover.component.html",
	styleUrls: ["./project-filter-popover.component.scss"],
	providers: [{ provide: SatPopoverAnchoringService, useExisting: false }],
})
export class ProjectFilterPopoverComponent extends SatPopover implements OnInit {
	constructor(
		private projectsQuery: ProjectsQuery,
		private userSettingsQuery: UserSettingsQuery,
		private projectsService: ProjectsService,
		private appService: AppService,
		private translateEntity: DefaultEntityTranslatePipe,
		_focusTrapFactory: ConfigurableFocusTrapFactory,
		_anchoringService: SatPopoverAnchoringService,
		_viewContainerRef: ViewContainerRef,
		@Optional() @Inject(DOCUMENT) _document: any,
		private cdr: ChangeDetectorRef
	) {
		super(
			_focusTrapFactory,
			_anchoringService,
			_viewContainerRef,
			"200ms cubic-bezier(0.25, 0.8, 0.25, 1)",
			_document
		);
	}

	search = new UntypedFormControl("", [Validators.required, Validators.minLength(2)]);
	completedLoading = false;
	completedShown = createRxValue(false);
	private completedShownBound = false;
	private _selectedItems = new BehaviorSubject<string[]>([]);
	readonly selectedItems$ = this._selectedItems.asObservable().pipe(distinctUntilChanged());

	get selectedItems() {
		return this._selectedItems.getValue();
	}

	@Input()
	set selectedItems(val: string[]) {
		const prev = this.selectedItems;
		this._selectedItems.next(val);

		if (xor(val, prev).length > 0) this.selectedItemsChange.emit(val);
	}

	@Input()
	set showCompleted(value: boolean) {
		this.completedShownBound = true;
		if (this.completedLoading) {
			return;
		}
		const next = !!value;
		if (this.completedShown.value !== next) {
			this.completedShown.value = next;
		}
	}
	get showCompleted(): boolean {
		return this.completedShown.value;
	}

	@Output() selectedItemsChange = new EventEmitter(true);
	@Output() showCompletedChange = new EventEmitter<boolean>(true);

	items$filtered = fromRxValue(
		combineLatest([
			this.search.valueChanges.pipe(startWith(this.search.value)),
			this.projectsQuery.selectAll({ filterBy: (x) => !x.completed }),
			this.projectsQuery.selectAll({ filterBy: (x) => x.completed }),
			this.selectedItems$,
			this.completedShown.asObservable(),
		]).pipe(
			map(
				([q, projects, projectsCompleted, selected, showCompleted]: [
					string,
					Project[],
					Project[],
					string[],
					boolean,
				]) => {
					return (() => {
						let returnProjects: Project[] = [...projects];
						if (showCompleted)
							returnProjects.push(
								...projectsCompleted.filter(
									(x) => returnProjects.findIndex((y) => y.id === x.id) === -1
								)
							);
						returnProjects = returnProjects.filter((p) => p.projectType == "project");
						const user = this.userSettingsQuery.getValue();
						const workspace = this.userSettingsQuery.getValue().workspace;

						const canSeePrivateTimes = hasPermissionByKey(user, "groupsCanSeePrivateTimes"),
							canManageAllProjects = hasPermissionByKey(user, "groupsCanManageAllProjects");

						if (canManageAllProjects) {
							if (!canSeePrivateTimes) {
								returnProjects = returnProjects.filter(
									(p) => isProjectUser(user.id, workspace, p) || !p.private
								);
							}
						}

						if (this.search.invalid) return returnProjects;
						const qLower = q.toLowerCase();
						return returnProjects.filter((x) => {
							const externalId = String(
								(x as Project & { externalId?: string | null }).externalId ?? ""
							).toLowerCase();
							return (
								this.translateEntity
									.transform(x, "project", "project.none")
									?.toLowerCase()
									.indexOf(qLower) !== -1 ||
								(x.client &&
									this.translateEntity
										.transform(x.client, "client", "client.none")
										?.toLowerCase()
										.indexOf(qLower) !== -1) ||
								externalId.indexOf(qLower) !== -1
							);
						});
					})()
						.map((x) => {
							return {
								...x,
								selected: selected.findIndex((u) => u === x.id) !== -1,
							};
						})
						.sort(
							firstBy("selected", "desc")
								// @ts-ignore
								.thenBy((x) => x.useAsDefault)
								.thenBy("client.name")
								.thenBy("name", "asc")
						);
				}
			)
		)
	);

	toggle() {
		this._open ? this.close() : this.open();
	}

	@Output() itemsClose = new EventEmitter(true);

	ngOnInit(): void {
		this.openAnimationStartAtScale = this.closeAnimationEndAtScale = 0.9375;
		this.hasBackdrop = true;
		this.yAlign = "below";
		this.xAlign = "center";
		this.closed.asObservable().subscribe(() => this.search.reset(""));
		this.closed
			.asObservable()
			.pipe(
				untilDestroyed(this),
				map(() => this.selectedItems),
				distinctUntilChanged()
			)
			.subscribe((val) => this.itemsClose.emit(val));
		if (!this.completedShownBound) {
			this.completedShown.value = this.projectsQuery.getCount((x) => x.completed) > 0;
			if (this.completedShown.value) this.completedLoading = false;
		}
	}

	trackId(index: number, { id }: { id: string }) {
		return id;
	}

	projectDisplayColor(project: Project & { color?: string | null }) {
		return resolveClientDisplayColor(project.color, project.id, (n) =>
			this.appService.colorScheme.getColorFromScheme(n)
		);
	}

	selectItem(id: string) {
		if (this.selectedItems.findIndex((x) => x === id) !== -1)
			this.selectedItems = this.selectedItems.filter((x) => x !== id);
		else this.selectedItems = [...this.selectedItems, id];
	}

	async toggleShowCompleted() {
		this.completedLoading = true;
		if (this.completedShown.value) {
			this.completedLoading = false;
			this.completedShown.value = false;
			this.showCompletedChange.emit(false);
		} else {
			this.projectsService
				.get(true)
				.toPromise()
				.then(() => {
					this.completedLoading = false;
					this.completedShown.value = true;
					this.showCompletedChange.emit(true);
				});
		}
	}

	selectAll() {
		this.selectedItems = this.items$filtered.value.map((x) => x.id);
	}

	selectNone() {
		this.selectedItems = [];
	}

	close() {
		super.close(this.selectedItems);
	}
}
